import type { MeterRead } from "@riea-water/contracts";
import { monthlyConsumption } from "./registry-helpers.js";

/**
 * Consumption · 24 months, as the prototype draws it: a bar per month from
 * the meter's reads, estimated months in the quiet tint, three gridlines
 * and three month labels. Pure SVG; nothing is read from outside the reads.
 */
export function ConsumptionChart(props: { reads: readonly MeterRead[]; asOf?: Date }) {
  const months = monthlyConsumption(props.reads, 24, props.asOf ?? new Date());
  const W = 640;
  const H = 170;
  const pl = 40;
  const pb = 24;
  const pt = 10;
  const max = Math.max(...months.map((m) => m.value), 0) * 1.15 || 1;
  const top = Math.ceil(max / 10) * 10 || 10;
  const bw = (W - pl - 8) / months.length;
  const y = (v: number) => pt + (H - pt - pb) * (1 - v / top);
  const labels = [0, 12, 23];
  return (
    <>
      <div className="chart">
        <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Monthly consumption, 24 months">
          {[0, top / 2, top].map((t) => (
            <g key={t}>
              <line x1={pl} x2={W - 4} y1={y(t)} y2={y(t)} stroke="var(--line)" />
              <text x={pl - 6} y={y(t) + 3} textAnchor="end">
                {t}
              </text>
            </g>
          ))}
          {months.map((m, i) => (
            <rect
              key={m.month}
              x={pl + i * bw + 1.5}
              y={y(m.value)}
              width={Math.max(0, bw - 3)}
              height={Math.max(0, y(0) - y(m.value))}
              rx={1.5}
              fill={m.estimated ? "var(--line2)" : "var(--accent)"}
              fillOpacity={m.estimated ? 1 : 0.85}
            >
              <title>{`${m.month}: ${m.value} m³${m.estimated ? " (estimated)" : ""}`}</title>
            </rect>
          ))}
          {labels.map((i) => (
            <text key={i} x={pl + i * bw + bw / 2} y={H - 6} textAnchor="middle">
              {months[i]?.month ?? ""}
            </text>
          ))}
        </svg>
      </div>
      <div className="legend">
        <span style={{ "--c": "var(--accent)" } as React.CSSProperties}>Actual, m³ per month</span>
        <span style={{ "--c": "var(--line2)" } as React.CSSProperties}>Estimated</span>
      </div>
    </>
  );
}
